import React, { useState, useEffect, useCallback } from "react";
import {
  Table, TableBody, TableCell, TableHead, TableRow,
  Paper, Typography, IconButton, Tabs, Tab,
  CircularProgress,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import ChatBubbleOutlineIcon from "@material-ui/icons/ChatBubbleOutline";
import HeadsetMicIcon from "@material-ui/icons/HeadsetMic";
import { toast } from "react-toastify";

import { adminListTickets, adminGetCounts } from "../../services/supportTicketService";
import SupportTicketChat from "../../components/SupportTicketChat";
import TableRowSkeleton from "../../components/TableRowSkeleton";

const PRIORITY_STYLES = {
  baixa: { background: "#dcfce7", color: "#166534" },
  media: { background: "#fef9c3", color: "#92400e" },
  alta: { background: "#fed7aa", color: "#9a3412" },
  urgente: { background: "#fee2e2", color: "#991b1b" },
};

const STATUS_STYLES = {
  aberto: { background: "#e0f2fe", color: "#0369a1" },
  em_atendimento: { background: "#f3e8ff", color: "#7c3aed" },
  aguardando_resposta: { background: "#fef9c3", color: "#92400e" },
  resolvido: { background: "#dcfce7", color: "#166534" },
  fechado: { background: "#f1f5f9", color: "#64748b" },
};

const STATUS_LABELS = {
  aberto: "Aberto", em_atendimento: "Em atendimento",
  aguardando_resposta: "Aguardando resposta", resolvido: "Resolvido", fechado: "Fechado",
};

const CATEGORY_LABELS = {
  tecnico: "Técnico", financeiro: "Financeiro", conta: "Conta",
  funcionalidade: "Funcionalidade", outro: "Outro",
};

const TABS = [
  { value: "todos", label: "Todos" },
  { value: "aberto", label: "Abertos" },
  { value: "em_atendimento", label: "Em atendimento" },
  { value: "aguardando_resposta", label: "Aguardando" },
  { value: "resolvido", label: "Resolvidos" },
  { value: "fechado", label: "Fechados" },
];

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(4),
    width: "100%",
    background: "#f8fafc",
    minHeight: "100vh",
  },
  pageHeader: { marginBottom: theme.spacing(3) },
  pageTitle: { fontSize: "28px", fontWeight: 700, color: "#1a1a2e", marginBottom: theme.spacing(0.5) },
  pageSubtitle: { fontSize: "14px", color: "#6b7280" },
  tabsRow: {
    background: "#fff", borderRadius: 12, padding: theme.spacing(0.5, 1),
    marginBottom: theme.spacing(3), boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
    "& .MuiTabs-indicator": { backgroundColor: theme.palette.primary.main, height: 3, borderRadius: 2 },
  },
  tab: {
    textTransform: "none", fontWeight: 500, fontSize: "13px", minHeight: 44,
    "&.Mui-selected": { color: theme.palette.primary.main, fontWeight: 700 },
  },
  content: { background: "#fff", borderRadius: 12, boxShadow: "0 2px 8px rgba(0,0,0,0.04)", overflow: "hidden" },
  tableHead: {
    "& th": {
      backgroundColor: "var(--sidebar-color, #1e293b)", color: "#fff",
      fontWeight: 600, fontSize: "0.8rem", textTransform: "uppercase",
      letterSpacing: "0.04em", padding: "10px 16px",
    },
  },
  badge: { padding: "2px 10px", borderRadius: 12, fontSize: "0.72rem", fontWeight: 700 },
  companyName: { fontWeight: 700, fontSize: "0.875rem", color: "#1e293b" },
  companyEmail: { fontSize: "0.75rem", color: "#94a3b8" },
  emptyBox: { textAlign: "center", padding: "60px 0" },
  emptyIcon: { fontSize: 56, color: "#cbd5e1", marginBottom: 12 },
  countBadge: {
    display: "inline-block", background: "#ef4444", color: "#fff",
    borderRadius: 10, fontSize: "0.65rem", fontWeight: 700,
    padding: "1px 6px", marginLeft: 4, verticalAlign: "middle",
  },
}));

const formatDate = (d) => new Date(d).toLocaleDateString("pt-BR", {
  day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
});

const SupportTicketsAdmin = () => {
  const classes = useStyles();

  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("todos");
  const [counts, setCounts] = useState({});
  const [chatTicketId, setChatTicketId] = useState(null);

  const sidebarColor = (() => {
    try { return window.__MUI_THEME__?.palette?.primary?.main || "#1e293b"; } catch { return "#1e293b"; }
  })();

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      adminListTickets(activeTab),
      adminGetCounts(),
    ]).then(([ticketsRes, countsRes]) => {
      setTickets(ticketsRes.data);
      setCounts(countsRes.data);
    }).catch(() => toast.error("Erro ao carregar chamados"))
      .finally(() => setLoading(false));
  }, [activeTab]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className={classes.root} style={{ "--sidebar-color": sidebarColor }}>
      <SupportTicketChat
        open={Boolean(chatTicketId)}
        onClose={() => setChatTicketId(null)}
        ticketId={chatTicketId}
        isAdmin={true}
        onUpdated={load}
      />

      <div className={classes.pageHeader}>
        <Typography className={classes.pageTitle}>Chamados de Suporte</Typography>
        <Typography className={classes.pageSubtitle}>
          Gerencie os chamados abertos pelas empresas clientes
        </Typography>
      </div>

      <div className={classes.tabsRow}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          variant="scrollable"
          scrollButtons="auto"
        >
          {TABS.map((tab) => (
            <Tab
              key={tab.value}
              value={tab.value}
              className={classes.tab}
              label={
                <span>
                  {tab.label}
                  {counts[tab.value] > 0 && (
                    <span className={classes.countBadge}>{counts[tab.value]}</span>
                  )}
                </span>
              }
            />
          ))}
        </Tabs>
      </div>

      <Paper className={classes.content}>
        <Table size="small">
          <TableHead className={classes.tableHead}>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Empresa</TableCell>
              <TableCell>Título</TableCell>
              <TableCell>Categoria</TableCell>
              <TableCell>Prioridade</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Atualizado</TableCell>
              <TableCell align="center">Atender</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRowSkeleton columns={8} />
            ) : tickets.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8}>
                  <div className={classes.emptyBox}>
                    <HeadsetMicIcon className={classes.emptyIcon} />
                    <Typography style={{ color: "#64748b", fontWeight: 600 }}>
                      Nenhum chamado nesta categoria
                    </Typography>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              tickets.map((t) => (
                <TableRow key={t.id} hover style={{ cursor: "pointer" }}
                  onClick={() => setChatTicketId(t.id)}>
                  <TableCell style={{ color: "#94a3b8", fontSize: "0.8rem" }}>#{t.id}</TableCell>
                  <TableCell>
                    <div className={classes.companyName}>{t.company?.name || `ID ${t.companyId}`}</div>
                    <div className={classes.companyEmail}>{t.company?.email}</div>
                  </TableCell>
                  <TableCell>
                    <Typography style={{ fontWeight: 600, fontSize: "0.875rem" }}>{t.title}</Typography>
                  </TableCell>
                  <TableCell>
                    <span style={{ color: "#64748b", fontSize: "0.8rem" }}>
                      {CATEGORY_LABELS[t.category] || t.category}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className={classes.badge} style={PRIORITY_STYLES[t.priority] || {}}>
                      {t.priority}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className={classes.badge} style={STATUS_STYLES[t.status] || {}}>
                      {STATUS_LABELS[t.status] || t.status}
                    </span>
                  </TableCell>
                  <TableCell style={{ fontSize: "0.8rem", color: "#64748b" }}>
                    {formatDate(t.updatedAt)}
                  </TableCell>
                  <TableCell align="center">
                    <IconButton size="small" color="primary"
                      onClick={(e) => { e.stopPropagation(); setChatTicketId(t.id); }}>
                      <ChatBubbleOutlineIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>
    </div>
  );
};

export default SupportTicketsAdmin;
